import type {
  MountedStorageStatus,
  MountErrorCode,
} from '@frameio/graphql-client/generated/types';
import { useActiveAccountId } from '../../../contexts';
import { useGetMountedStorageSettingsQuery } from '../gql/generated/GetMountedStorageSettingsQuery';

export type AccountMountedStorageStatus = {
  shouldEnable: boolean;
  shouldHide: boolean;
  isAccountEnabled: boolean;
  projectStatus?: MountedStorageStatus | null;
  /** Highest-priority mount error to surface, if any. See MOUNT_ERROR_PRIORITY. */
  error?: MountErrorCode;
  /** Whether the project can be mounted, i.e. there is no blocking mount error */
  canMount: boolean;
};

/**
 * Priority order for selecting which single mount error to surface when
 * multiple are present simultaneously. Mirrors drive-app-client's
 * MOUNT_ERROR_PRIORITY (src/renderer/utils/mountedStorage.ts).
 */
const MOUNT_ERROR_PRIORITY: MountErrorCode[] = [
  'NO_MOUNTED_STORAGE_ENTITLEMENT',
  'MOUNTED_STORAGE_DISABLED',
  'ACCOUNT_LOCKED',
  'ACCOUNT_DELINQUENT',
  'STORAGE_QUOTA_EXCEEDED',
  'PROJECT_LOCKED',
  'CONTENT_SECURITY_RESTRICTION',
  'INSUFFICIENT_PERMISSIONS',
];

/**
 * Returns the mounted storage UI state for a given project, derived from
 * account-level enablement and project-level status/permissions.
 *
 * Decision logic:
 *
 *                        account enabled?
 *                       /               \
 *                     YES               NO
 *                     /                   \
 *          project locked?             shouldHide
 *          /           \
 *        YES            NO
 *         |              |
 *   shouldEnable=false  shouldEnable
 *   (disabled via
 *    canMount/error,
 *    still rendered)
 *
 * `shouldHide` only reflects account-level enablement — a locked project
 * (`projectStatus === 'LOCKED'`) should still render, disabled, since the
 * backend already surfaces that via `canMount`/`mountErrors` (PROJECT_LOCKED).
 *
 * Returns:
 *   - `shouldEnable`   — render in an active/clickable state
 *   - `shouldHide`     — do not render
 *   - `isAccountEnabled` — raw account-level flag
 *   - `projectStatus`    — raw project-level status
 *   - `error`            — error code if project cannot be mounted
 *   - `canMount`         — can the project be mounted?
 */
export function useMountedStorageStatus(
  projectId?: string
): AccountMountedStorageStatus {
  const accountId = useActiveAccountId() ?? '';

  const { data } = useGetMountedStorageSettingsQuery({
    variables: { accountId, projectId: projectId ?? '' },
    skip: !accountId || !projectId,
  });

  const mountedStorageEnabled = data?.account?.mountedStorageEnabled;
  const isAccountEnabled = mountedStorageEnabled ?? true;
  const project = data?.account?.project;
  const projectStatus = project?.mountedStorageStatus;
  const isProjectLocked = projectStatus === 'LOCKED';
  const canMount = project?.permissions?.canMount ?? true;
  const mountErrors = project?.permissions?.mountErrors ?? [];
  const error = MOUNT_ERROR_PRIORITY.find((code) => mountErrors.includes(code));

  return {
    shouldEnable: isAccountEnabled && !isProjectLocked,
    // Hide until the account flag resolves; the optimistic default above would
    // otherwise show the item and then remove it once the query loads.
    shouldHide: mountedStorageEnabled !== true,
    isAccountEnabled,
    projectStatus,
    error,
    canMount,
  };
}
